import {useCallback} from 'react'

import {queryOptions, useQuery} from '@tanstack/react-query'
import {addMonths, differenceInCalendarDays} from 'date-fns'

import {MYASSET_APIS} from '$constants/api-infos'
import {ExpireIndustry} from '$constants/connectExpire'
import {internalApi} from '$utils/api'
import {ServerDate, convertToDate} from '$utils/date'

import type {ExpiresStatus, GetExpireStatusParams} from '$constants/api-infos'
import type {IncomingMessage} from 'http'

const {GET_CONNECT_EXPIRES} = MYASSET_APIS

export async function getExpireStatus(params?: GetExpireStatusParams, req?: IncomingMessage) {
    const {path, method} = GET_CONNECT_EXPIRES
    const {data} = await internalApi<ExpiresStatus>(path, {method, params, req})
    return data
}

export function getExpireStatusQueryOption(params?: GetExpireStatusParams, req?: IncomingMessage) {
    const {queryKey} = GET_CONNECT_EXPIRES
    return queryOptions({queryKey, queryFn: () => getExpireStatus(params, req)})
}

export const getExpiredAsset = (assetType: ExpireIndustry, data?: ExpiresStatus) =>
    !!data?.[assetType] && differenceInCalendarDays(convertToDate(data[assetType] || ''), ServerDate()) < 0

export function useExpireStatusQuery(params?: GetExpireStatusParams, req?: IncomingMessage) {
    const response = useQuery(getExpireStatusQueryOption(params, req))

    const isExpiredAsset = useCallback(
        (assetType: ExpireIndustry) => {
            return getExpiredAsset(assetType, response.data)
        },
        [response.data],
    )

    const getOrgExpireInfo = useCallback(
        (orgCode: string) => {
            let organizationExpirationInfo = {
                expired: false,
                endDate: addMonths(new Date(), 2),
                diffDays: 100,
            }

            const consent = response.data?.consents?.find(({org}) => org.orgCode === orgCode)

            if (consent) {
                const endDate = consent.endDate ? convertToDate(consent.endDate) : addMonths(new Date(), 2)
                const diff = differenceInCalendarDays(endDate, ServerDate())
                organizationExpirationInfo = {
                    expired: diff < 0,
                    endDate,
                    diffDays: diff,
                }
            }

            return organizationExpirationInfo
        },
        [response],
    )

    return {
        ...response,
        getOrgExpireInfo,
        isExpiredAsset,
    }
}

const ALL_INDUSTRIES = [
    ExpireIndustry.BANK,
    ExpireIndustry.CARD,
    ExpireIndustry.INVEST,
    ExpireIndustry.INSU,
    ExpireIndustry.TELECOM,
]

export function toExpireDays(expireStatus: ExpiresStatus, industries: ExpireIndustry[] = ALL_INDUSTRIES) {
    const shortestDate = industries.reduce(
        (date, industry) => {
            const key = industry as keyof ExpiresStatus

            if (expireStatus[key]) {
                const expireDateString = expireStatus[key] as string
                const expireDate = convertToDate(expireDateString)

                if (expireDate.getTime() < date.getTime()) {
                    return expireDate
                }
            }

            return date
        },
        addMonths(new Date(), 2),
    )

    const diff = differenceInCalendarDays(shortestDate, ServerDate())

    return {
        expired: diff < 0,
        shortestDate,
        diffDays: diff,
    }
}
